import React, { useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { partnershipsPageFooter } from './layout/footerConfigs'
import { CustomSelect } from './ui/CustomSelect'

export const PartnershipsPage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const { scrollProgress } = useOptimizedScroll()

  return (
    <>
      <Helmet>
        <title>Partnerships - Work With Mernaki</title>
        <meta 
          name="description" 
          content="Partner with Mernaki. We collaborate with pioneers in technology, energy, and digital innovation — building systems that connect industries, creators, and intelligence." 
        />
      </Helmet>

      <motion.div 
        className="min-h-screen relative"
        style={{ 
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transform: `translateY(${scrollProgress * -20}px)`
        }}
        transition={{ duration: 0 }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div 
          className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            top: '15%',
            left: '8%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -80, 60, 0],
            y: [0, 70, -50, 0],
            scale: [1, 1.3, 0.85, 1]
          }}
          transition={{ duration: 27, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div 
          className="absolute w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            top: '50%',
            right: '5%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 90, -70, 0],
            y: [0, -60, 80, 0],
            scale: [1, 0.9, 1.2, 1]
          }}
          transition={{ duration: 32, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div 
          className="absolute w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            bottom: '15%',
            left: '12%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 70, -90, 0],
            y: [0, -80, 50, 0],
            scale: [1, 1.15, 0.9, 1]
          }}
          transition={{ duration: 29, repeat: Infinity, ease: 'easeInOut' }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />
        <FocusAreasSection prefersReducedMotion={prefersReducedMotion.current} />
        <WhyPartnerSection prefersReducedMotion={prefersReducedMotion.current} />
        <BecomePartnerSection prefersReducedMotion={prefersReducedMotion.current} />
      </motion.div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  return (
    <section ref={ref} className="relative pt-56 pb-32 overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 
          className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Partnering for a Smarter Future
        </motion.h1>
        
        <motion.p 
          className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed mb-12" 
          style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          We collaborate with pioneers in technology, energy, and digital innovation — building systems that connect industries, creators, and intelligence.
        </motion.p>
        
        <motion.div 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <motion.a
            href="#become-partner"
            className="px-8 py-4 rounded-xl text-base inline-block"
            style={{
              backgroundColor: '#1A1A1A',
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              textDecoration: 'none'
            }}
            whileHover={prefersReducedMotion ? {} : { 
              scale: 1.05,
              y: -2,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
            }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333333'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1A1A1A'}
          >
            Become a Partner
          </motion.a>
        </motion.div>
      </div>
    </section>
  )
}

const FocusAreasSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const areas = [
    {
      title: 'Services',
      description: 'Work with us to expand compliance automation, environmental intelligence, and enterprise integrations.'
    },
    {
      title: 'Software',
      description: 'Integrate our developer tools — from Project Crimson to Creator Tools ecosystem — into your workflow.',
      isBlurred: true
    },
    {
      title: 'Hardware',
      description: 'Collaborate on Project Nexus design, embedded systems, or next-gen portable computing.',
      isBlurred: true
    }
  ]
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-16 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Our Partnership Focus Areas
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {areas.map((area, index) => (
            <motion.div
              key={index}
              className="p-8 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
            >
              <h3 
                className="text-2xl mb-3" 
                style={{ 
                  color: area.isBlurred ? 'rgba(26, 26, 26, 0.3)' : '#1A1A1A', 
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", 
                  fontWeight: 600,
                  filter: area.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {area.title}
              </h3>
              <p 
                className="text-base" 
                style={{ 
                  color: area.isBlurred ? 'rgba(102, 102, 102, 0.3)' : '#666666', 
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", 
                  fontWeight: 400,
                  filter: area.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {area.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const WhyPartnerSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const benefits = [
    {
      title: 'Trusted Infrastructure',
      description: 'Secure, EU-hosted architecture built for speed and privacy'
    },
    {
      title: 'AI-Integrated Systems',
      description: 'Every platform powered by intelligent automation'
    },
    {
      title: 'Fast Collaboration',
      description: 'Instant access to unified APIs and real-time support'
    },
    {
      title: 'Co-Brand Opportunities',
      description: 'Launch white-label or integrated joint products'
    }
  ]
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-16 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Why Partner with Mernaki
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="p-8 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
            >
              <h3 
                className="text-2xl mb-3" 
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                {benefit.title}
              </h3>
              <p 
                className="text-base" 
                style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
              >
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const BecomePartnerSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    areaOfInterest: 'services',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      // Get the API URL from environment or use default
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
      
      // Send form data to backend API
      const response = await fetch(`${apiUrl}/api/partnerships/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (response.ok && result.success) {
        // Show success message
        setSubmitStatus('success')
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            companyName: '',
            website: '',
            areaOfInterest: 'services',
            message: ''
          })
          setSubmitStatus('idle')
        }, 3000)
      } else {
        throw new Error(result.error || 'Failed to submit application')
      }
      
    } catch (error) {
      console.error('Error submitting form:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }
  
  return (
    <section id="become-partner" ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="p-12 rounded-3xl"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 
            className="text-3xl md:text-4xl mb-4 text-center" 
            style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
          >
            Let's Build Together
          </h2>
          <p 
            className="text-lg mb-8 text-center" 
            style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
          >
            Tell us a bit about your organization, and we'll get in touch within 24 hours.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Name */}
            <div>
              <label 
                htmlFor="companyName" 
                className="block text-sm mb-2"
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                Company Name <span style={{ color: '#E74C3C' }}>*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                required
                value={formData.companyName}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl transition-all duration-200"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  color: '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontSize: '15px'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
            </div>

            {/* Website / LinkedIn */}
            <div>
              <label 
                htmlFor="website" 
                className="block text-sm mb-2"
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                Website / LinkedIn <span style={{ color: '#E74C3C' }}>*</span>
              </label>
              <input
                type="url"
                id="website"
                name="website"
                required
                value={formData.website}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl transition-all duration-200"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  color: '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontSize: '15px'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
            </div>

            {/* Area of Interest */}
            <div className="relative z-10">
              <label 
                htmlFor="areaOfInterest" 
                className="block text-sm mb-2"
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                Area of Interest <span style={{ color: '#E74C3C' }}>*</span>
              </label>
              <CustomSelect
                value={formData.areaOfInterest}
                options={[
                  { value: 'services', label: 'Services' },
                  { value: 'software', label: 'Software' },
                  { value: 'hardware', label: 'Hardware' },
                  { value: 'multiple', label: 'Multiple Areas' }
                ]}
                onChange={(val) => setFormData({ ...formData, areaOfInterest: val })}
              />
            </div>

            {/* Message */}
            <div>
              <label 
                htmlFor="message" 
                className="block text-sm mb-2"
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                Message <span style={{ color: '#E74C3C' }}>*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl transition-all duration-200 resize-none"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  color: '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontSize: '15px'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(74, 144, 226, 0.5)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
            </div>

            {/* Status Message */}
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl text-center"
                style={{
                  backgroundColor: 'rgba(46, 204, 113, 0.1)',
                  border: '1px solid rgba(46, 204, 113, 0.3)',
                  color: '#2ECC71',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 500
                }}
              >
                ✓ Thank you! Your partnership application has been submitted successfully. We'll get back to you within 24 hours.
              </motion.div>
            )}
            
            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl text-center"
                style={{
                  backgroundColor: 'rgba(231, 76, 60, 0.1)',
                  border: '1px solid rgba(231, 76, 60, 0.3)',
                  color: '#E74C3C',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 500
                }}
              >
                ✗ Something went wrong. Please try again or email us directly at partnership.jb@mernaki.de
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 px-8 rounded-xl text-base"
              style={{
                backgroundColor: isSubmitting ? '#666666' : '#1A1A1A',
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1
              }}
              whileHover={prefersReducedMotion || isSubmitting ? {} : { 
                scale: 1.02,
                y: -2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
              }}
              whileTap={prefersReducedMotion || isSubmitting ? {} : { scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = '#333333')}
              onMouseLeave={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = '#1A1A1A')}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  if (isMobile) {
    return <PageMobileFooter {...partnershipsPageFooter} />
  }
  
  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', marginTop: '-80px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Footer Header */}
        <motion.div 
          className="text-center mb-12 pb-8 border-b"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h3 
            className="text-2xl mb-2"
            style={{ 
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '-0.02em'
            }}
          >
            Partnerships
          </h3>
          <p 
            className="text-sm"
            style={{ 
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 400
            }}
          >
            Building the future together
          </p>
        </motion.div>

        <div className="py-16">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            {/* Column 1 - Partnership */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Partnership
              </h3>
              <ul className="space-y-3">
                <li><a href="mailto:partnership.jb@mernaki.de" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>partnership.jb@mernaki.de</a></li>
                <li><Link to="/partnerships" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Partner Program</Link></li>
                <li><Link to="/about" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>About</Link></li>
              </ul>
            </div>

            {/* Column 2 - Products */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Products
              </h3>
              <ul className="space-y-3">
                <li><Link to="/#services" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Services</Link></li>
                <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Software</span></li>
                <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Hardware</span></li>
                <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Press</span></li>
              </ul>
            </div>

            {/* Column 3 - Legal */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Legal
              </h3>
              <ul className="space-y-3">
                <li><Link to="/privacy-policy" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Terms of Service</Link></li>
                <li><Link to="/impressum" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Impressum</Link></li>
              </ul>
            </div>
          </motion.div>

          {/* Showcase Row */}
          <div className="mb-10 pb-10 border-b border-white/10">
            <div className="flex items-center justify-center gap-3 flex-wrap text-sm">
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Crimson</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Velocity</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Creator Tools</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Nexus</span>
            </div>
          </div>

          {/* Bottom Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col md:flex-row justify-between items-center gap-6"
          >
            <div className="flex items-center gap-3">
              <img src="/Mernaki_Logo_Pack/transparent/mernaki-128.png" alt="Mernaki Logo" className="object-contain" style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }} loading="lazy" />
              <h3 className="text-lg" style={{ color: '#FFFFFF', fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive", fontWeight: 400 }}>Mernaki</h3>
            </div>
            <p className="text-sm text-center" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 300 }}>© {new Date().getFullYear()} Mernaki. All rights reserved. Munich, Germany.</p>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}
